import TableContainer from "Common/Tables/StringsFiltersTable/TableContainer";
import ActionButton from "components/Buttons/ActionButton";
import { EditButton } from "components/Buttons/EditButton";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import ListingPageContainer from "components/PageContainer/ListingPageContainer";
import SearchBox from "components/SearchBox/SearchBox";
import StatusToggle from "components/StatusToggle";
import useClearStateOnNavigation from "CustomHooks/useClearStateOnNavigation";
import usePermission from "CustomHooks/usePermission";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { app_modules } from "helpers/apps_modules_list";
import { useEffect, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { createSelector } from "reselect";
import {
  exportHsnCodeCSV,
  fetchhsnCodes as OnfetchHsnCodes,
  updateStatusInhsnCode as onUpdateStatusInHsnCode,
} from "slices/hsn-code/thunk";
import Swal from "sweetalert2";
import { showCustomSwal } from "utils/swalHelper";

const HSNCode = () => {
  document.title = "HSNCode List";
  const dispatch = useDispatch<any>();
  const location = useLocation();

  // Use the preserved table state hook
  const tableState = usePreservedTableState({
    key: "hsnCode",
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue: colValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue: setColValue,
    resetState,
  } = tableState;

  // Redux selector for hsn-code state
  const selectHsnCodes = createSelector(
    (state: any) => state.HSNCode,
    (hsnCode) => ({
      HsnCodes: hsnCode.hsnCodeList,
      rowCount: hsnCode.rowCount,
      loading: hsnCode.loading,
      error: hsnCode.error,
    })
  );

  const { HsnCodes, rowCount, loading, error } = useSelector(selectHsnCodes);

  // Use custom hook to fetch and access permissions
  const { canView, canCreate, canUpdate } = usePermission(
    app_modules?.master?.app_name,
    app_modules?.master?.modules?.hsn_code
  );

  const [data, setData] = useState([]);

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  useEffect(() => {
    dispatch(
      OnfetchHsnCodes({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: colValue,
      })
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    search,
    colValue,
  ]);

  useEffect(() => {
    setData(HsnCodes);
  }, [HsnCodes]);

  // Clear state when navigating from create action (not edit)
  useClearStateOnNavigation(location, resetState);

  // Debounce search input to actual search
  useEffect(() => {
    // Skip if searchInput already matches search (prevents unnecessary updates during restoration)
    if (searchInput === search) {
      return;
    }

    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput, setSearch, search]);

  const handleStatusToggle = async (pkg: any) => {
    try {
      const updatedStatus = !pkg.is_active;
      const updatedHsnCode = {
        id: pkg.hsn_code_id,
        is_active: updatedStatus,
      };
      await dispatch(onUpdateStatusInHsnCode(updatedHsnCode))
        .unwrap()
        .then(() => {
          toast.success(
            `HSN Code ${
              updatedStatus ? "activated" : "deactivated"
            } successfully`
          );
        });
      dispatch(
        OnfetchHsnCodes({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: colValue,
        })
      );
    } catch (err: any) {
      toast.error("Failed to Update Status");
    }
  };

  const handleExport = async () => {
    // Show loading modal
    showCustomSwal({
      title: "Exporting...",
      text: "Please wait while we export your data.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const { blob, filename } = await exportHsnCodeCSV({
        search,
        filters: colValue,
      });

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Replace loading modal with success modal
      showCustomSwal({
        icon: "success",
        title: "Export Completed",
        text: "Export completed successfully.",
      });
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to export CSV";

      // Replace loading modal with error modal
      showCustomSwal({
        icon: "error",
        title: "Export Failed",
        text: errorMessage,
      });
    }
  };

  const DynamicAction = canUpdate
    ? [
        {
          header: "Action",
          enableColumnFilter: false,
          enableSorting: false,
          cell: ({ row }: { row: any }) => (
            <EditButton to={`/edit-hsn-code/${row?.original?.hsn_code_id}`} />
          ),
        },
      ]
    : [];

  const columns =
    // useMemo(
    //   () =>
    [
      {
        header: "Sr. No.",
        id: "sr_no", // important: since there's no accessorKey
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }: { row: any }) => {
          return (
            row.original.sr_no || row.index + 1 + pageIndex * pageSize || "-"
          );
        },
      },

      {
        header: "HSN Code",
        accessorKey: "hsn_code",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/master/hsn/dropdown/hsn_code/",
      },
      {
        header: "HSN Code Description",
        accessorKey: "description",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => (
          <div
            style={{
              whiteSpace: "normal",
              wordWrap: "break-word",
              width: "250px",
            }}
          >
            {row.original.description || "-"}
          </div>
        ),
        // filterType: "dropdown" as const,
        // filterFetchUrl: "hsn/dropdown/hsn_code_description/",
      },
      {
        header: "GST%",
        accessorKey: "gst_percent",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/master/hsn/dropdown/gst_percent",
        cell: ({ row }: { row: any }) =>
          `${row.original.gst_percent.gst_percent} %`,
      },
      {
        header: "Status",
        accessorKey: "is_active",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const pkg = row.original;
          return (
            <StatusToggle
              isActive={pkg.is_active}
              onChange={() => {
                handleStatusToggle(pkg);
              }}
              disabled={!canUpdate}
            />
          );
        },
      },
      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.created_by_user.username ?? "N/A";
          const date = row.original.created_at
            ? new Date(row.original.created_at).toISOString().split("T")[0]
            : "N/A";
          return `${name}-${date}`;
        },
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.updated_by_user.username ?? "N/A";
          const date = row.original.updated_at
            ? new Date(row.original.updated_at).toISOString().split("T")[0]
            : "N/A";
          return `${name}-${date}`;
        },
      },
      ...DynamicAction,
    ];
  //   [dispatch, HsnCodes]
  // );

  return (
    <ListingPageContainer>
      <Row id="invoiceList">
        <Col lg={12}>
          <Card>
            <Card.Header>
              <Row className="align-items-center g-2">
                <Col lg={3} className="me-auto">
                  <h6 className="card-title mb-0">HSN Code</h6>
                </Col>

                {/* Search Input Box (reusable)*/}
                <Col xl={2} md={3}>
                  <SearchBox value={searchInput} onChange={setSearchInput} />
                </Col>

                <Col className="col-md-auto">
                  <div className="hstack gap-2">
                    {canView && (
                      <ActionButton
                        label="Export"
                        icon="bi bi-box-arrow-up"
                        handleClick={handleExport}
                        variant="light"
                      />
                    )}
                    {canCreate && (
                      <ActionButton
                        label="Add HSN Code"
                        icon="bi bi-plus-circle"
                        navigateLink="/add-hsn-code"
                        variant="primary"
                      />
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="mt-3">
              <LoaderErrorWrapper loading={loading} error={error}>
                <TableContainer
                  columns={columns}
                  data={data}
                  totalRecords={rowCount}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  onPageChange={setPageIndex}
                  onPageSizeChange={setPageSize}
                  columnFilters={columnFilters}
                  setColumnFilters={setColumnFilters}
                  sorting={sorting}
                  onSortingChange={setSorting}
                  isBordered={false}
                  isPagination={true}
                  isGlobalFilter={false}
                  SearchPlaceholder="Search HSN Codes..."
                  manualSorting={true}
                  colValue={setColValue}
                />
              </LoaderErrorWrapper>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </ListingPageContainer>
  );
};

export default HSNCode;
